<script setup lang="ts">
import { router, useForm, usePage } from '@inertiajs/vue3'
import { Head } from '@inertiajs/vue3'
import { Camera, Trash2 } from 'lucide-vue-next'
import { computed, ref } from 'vue'

import DeleteUser from '@/components/DeleteUser.vue'
import HeadingSmall from '@/components/HeadingSmall.vue'
import InputError from '@/components/InputError.vue'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useInitials } from '@/composables/useInitials'
import AppLayout from '@/layouts/AppLayout.vue'
import SettingsLayout from '@/layouts/settings/Layout.vue'
import { avatarSettingsProfilePath, settingsProfilePath } from '@/routes'
import { type BreadcrumbItem, type User } from '@/types'

const breadcrumbs: BreadcrumbItem[] = [{ title: 'Profile settings', href: settingsProfilePath() }]

const page = usePage()
const user = computed(() => page.props.auth.user as User)
const { getInitials } = useInitials()

const form = useForm({ name: user.value.name, avatar: null as File | null })

const preview = ref<string | null>(null)
const fileInput = ref<HTMLInputElement | null>(null)

const MAX_SIZE_MB = 5
const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp']
const sizeError = ref<string | null>(null)

function pickFile() { fileInput.value?.click() }

function onFileChange(e: Event) {
  const file = (e.target as HTMLInputElement).files?.[0] ?? null
  sizeError.value = null
  if (!file) return
  if (!ACCEPTED.includes(file.type)) {
    sizeError.value = 'Only JPEG, PNG, or WebP images are accepted.'
    return
  }
  if (file.size > MAX_SIZE_MB * 1024 * 1024) {
    sizeError.value = `Image must be under ${MAX_SIZE_MB} MB.`
    return
  }
  form.avatar = file
  preview.value = URL.createObjectURL(file)
}

function removeAvatar() {
  router.delete(avatarSettingsProfilePath(), { preserveScroll: true })
}

function submit() {
  form.patch(settingsProfilePath(), { preserveScroll: true })
}

function err(field: string): string[] | undefined {
  const msg = (form.errors as Record<string, string | undefined>)[field]
  return msg ? [msg] : undefined
}
</script>

<template>
  <AppLayout :breadcrumbs="breadcrumbs">
    <Head :title="breadcrumbs[breadcrumbs.length - 1].title" />

    <SettingsLayout>
      <div class="flex flex-col space-y-6">
        <HeadingSmall title="Profile information" description="Update your name and profile photo" />

        <form class="space-y-6" @submit.prevent="submit">
          <!-- Avatar -->
          <div class="flex items-center gap-5">
            <Avatar class="size-20 rounded-xl">
              <AvatarImage v-if="preview || user.avatar" :src="(preview ?? user.avatar)!" :alt="user.name" />
              <AvatarFallback class="rounded-xl text-lg">{{ getInitials(user.name) }}</AvatarFallback>
            </Avatar>

            <div class="space-y-2">
              <div class="flex items-center gap-2">
                <Button type="button" variant="outline" size="sm" @click="pickFile">
                  <Camera class="size-4" />
                  {{ user.avatar || preview ? 'Change photo' : 'Upload photo' }}
                </Button>
                <Button
                  v-if="user.avatar && !preview"
                  type="button"
                  variant="ghost"
                  size="sm"
                  class="text-muted-foreground hover:text-destructive"
                  @click="removeAvatar"
                >
                  <Trash2 class="size-4" />
                  Remove
                </Button>
              </div>
              <p class="text-muted-foreground text-xs">JPEG, PNG or WebP · max {{ MAX_SIZE_MB }} MB</p>
              <p v-if="sizeError" class="text-destructive text-xs">{{ sizeError }}</p>
            </div>

            <input
              ref="fileInput"
              type="file"
              :accept="ACCEPTED.join(',')"
              class="sr-only"
              @change="onFileChange"
            />
          </div>

          <!-- Name -->
          <div class="grid gap-2">
            <Label for="name">Name</Label>
            <Input
              id="name"
              v-model="form.name"
              class="mt-1 block w-full max-w-sm"
              required
              autocomplete="name"
              placeholder="Full name"
            />
            <InputError class="mt-2" :messages="err('name')" />
          </div>

          <div class="flex items-center gap-4">
            <Button type="submit" :disabled="form.processing">Save</Button>
            <Transition enter-active-class="transition ease-in-out" enter-from-class="opacity-0" leave-active-class="transition ease-in-out" leave-to-class="opacity-0">
              <p v-show="form.recentlySuccessful" class="text-muted-foreground text-sm">Saved.</p>
            </Transition>
          </div>
        </form>
      </div>

      <DeleteUser />
    </SettingsLayout>
  </AppLayout>
</template>
